/* ==========================================================================
   maai — theme.js
   Vanilla JS. No framework, no build step — matches the "fast, restrained"
   direction. Handles: scroll reveal, the product gallery (lightbox, mobile
   dots), quantity steppers, mobile nav, the cart drawer (via Shopify's
   Ajax Cart API), and inline audio previews.
   ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {

  /* ---------------- Scroll reveal ---------------- */
  // .reveal elements start at opacity:0 and get their .in class either
  // here (immediately, for anything already on screen) or later from the
  // IntersectionObserver below (for anything the visitor scrolls to).
  //
  // The check for "already on screen at load" deliberately does NOT reuse
  // the observer's own 0.12 (12%-of-its-own-height) threshold. That ratio
  // is fine for a normal, viewport-sized reveal target, but some .reveal
  // targets here are entire sections wrapping a whole product grid --
  // often many times taller than the viewport. On one of those, the
  // visible sliver at load is a small fraction of that huge total height,
  // so a "some fraction is visible" style check reports it as "not yet
  // visible" even though its top is sitting right there on screen. The
  // right question for "should this already be revealed" is simply "does
  // it overlap the viewport at all" -- not "what percentage of it shows".
  var revealIO = null;

  var overlapsViewport = function (el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var vw = window.innerWidth || document.documentElement.clientWidth;
    return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
  };

  var revealIfOnscreen = function (el) {
    if (el.classList.contains('in')) return;
    if (overlapsViewport(el)) {
      el.classList.add('in');
      if (revealIO) revealIO.unobserve(el);
    }
  };

  var settleAllReveals = function () {
    document.querySelectorAll('.reveal').forEach(revealIfOnscreen);
  };

  var initReveal = function (scope) {
    var els = (scope || document).querySelectorAll('.reveal:not([data-reveal-ready])');
    if (!els.length) return;

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.setAttribute('data-reveal-ready', '');
        el.classList.add('in');
      });
      return;
    }

    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            revealIO.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
    }

    els.forEach(function (el) {
      el.setAttribute('data-reveal-ready', '');
      revealIO.observe(el);
    });

    // Double rAF: the first callback only guarantees we're on the frame
    // the browser is about to paint, not that this content's layout has
    // actually been committed yet. Nesting a second one guarantees we run
    // on the frame AFTER that -- i.e. after real layout is available --
    // which is exactly the timing IntersectionObserver's own first
    // callback isn't reliably guaranteed to land on in every engine (the
    // root cause of "frozen until I resize/zoom").
    requestAnimationFrame(function () {
      requestAnimationFrame(settleAllReveals);
    });
  };

  initReveal(document);

  // Failsafe reconciliation: web fonts and images can still shift layout
  // after the check above runs. Sweep once more once everything has
  // fully loaded, so nothing that should already be visible is left
  // behind. This never touches elements that are legitimately below the
  // fold -- overlapsViewport() only reveals what's actually on screen.
  if (document.readyState === 'complete') {
    settleAllReveals();
  } else {
    window.addEventListener('load', settleAllReveals, { once: true });
  }

  // Back/forward-cache restores (Safari and Chrome both do this for
  // simple pages like collection/product pages) do NOT re-fire
  // DOMContentLoaded -- the page is restored from memory as-is. If a
  // reveal never fired before the page was frozen, this is the only
  // place that can still catch it.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) settleAllReveals();
  });

  /* ---------------- Type-on text reveal ---------------- */
  // Character-by-character reveal for display typography: wordmark, page/
  // collection/product titles, section headings, short editorial lines.
  // Opt-in via a single data-type-on attribute in markup — nothing here
  // hunts for headings on its own, so it never touches prices, buttons,
  // forms, nav, or cart/legal text that don't carry the attribute.
  //
  // Progressive enhancement: Liquid already renders the real text, so it's
  // present, readable, and indexable with or without JS. This only wraps
  // that existing text into per-character spans and reveals them in
  // sequence via CSS transition-delay (no per-character timers). The
  // element's own aria-label carries the real text and each character
  // span is aria-hidden, so screen readers announce the whole word/line
  // normally rather than one letter at a time.
  //
  // To remove this feature: delete this block and the matching CSS
  // (".tc" / "[data-type-on]" rules), or just drop data-type-on from
  // markup to stop applying it in specific places.
  var typeOnReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var initTypeOn = function (scope) {
    var els = (scope || document).querySelectorAll('[data-type-on]:not([data-type-on-ready])');
    if (!els.length) return;

    if (typeOnReducedMotion || !('IntersectionObserver' in window)) {
      // Reduced motion (or no IO support): leave the text exactly as
      // rendered — no wrapping, no animation, fully readable immediately.
      els.forEach(function (el) {
        el.setAttribute('data-type-on-ready', '');
        el.classList.add('is-in');
      });
      return;
    }

    var targets = [];
    els.forEach(function (el) {
      var text = el.textContent;
      if (!text || !text.trim()) return;

      el.setAttribute('aria-label', text);
      var visual = document.createElement('span');
      visual.setAttribute('aria-hidden', 'true');
      visual.className = 'type-on-visual';

      var i = 0;
      Array.prototype.forEach.call(Array.from(text), function (ch) {
        if (ch === ' ') {
          visual.appendChild(document.createTextNode(' '));
          return;
        }
        var span = document.createElement('span');
        span.className = 'tc';
        span.textContent = ch;
        span.style.setProperty('--i', i);
        span.style.setProperty('--j', Math.floor(Math.random() * 6)); // 0–5ms jitter, keeps the stagger from feeling mechanically uniform
        visual.appendChild(span);
        i++;
      });

      el.textContent = '';
      el.appendChild(visual);
      el.setAttribute('data-type-on-ready', '');
      targets.push(el);
    });

    var typeOnObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          typeOnObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    // Same synchronous first-paint check as the scroll-reveal observer
    // above, and for the same reason: text already on screen at load
    // shouldn't wait on an engine's first IntersectionObserver callback,
    // which can be deferred until a later compositing pass.
    var isTypeOnVisibleNow = function (el) {
      var r = el.getBoundingClientRect();
      if (r.height <= 0) return false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      return (visible / r.height) >= 0.1; // matches the observer's own threshold
    };

    targets.forEach(function (el) {
      if (isTypeOnVisibleNow(el)) {
        el.classList.add('is-in');
      } else {
        typeOnObserver.observe(el);
      }
    });
  };

  initTypeOn(document);

  // Shopify Theme Editor re-injects a section's HTML (fresh, unwrapped
  // text/markup) whenever it's edited or reloaded in the customizer
  // preview — re-run initialization scoped to just that section so both
  // the reveal and type-on animations keep working there, without
  // double-wrapping/double-observing anything already initialized
  // elsewhere on the page.
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initTypeOn(e.target);
  });

  /* ---------------- Product gallery: lightbox + mobile dots ---------------- */
  var lightbox = document.querySelector('[data-lightbox]');
  if (lightbox) {
    var lightboxImg = lightbox.querySelector('[data-lightbox-img]');

    var openLightbox = function (src, alt) {
      lightboxImg.src = src;
      lightboxImg.alt = alt || '';
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    var closeLightbox = function () {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-lightbox-trigger]').forEach(function (img) {
      img.addEventListener('click', function () {
        // On the mobile swipe strip a tap is for browsing, not zooming —
        // only open the lightbox where there's a gallery to the side.
        if (window.matchMedia('(min-width: 861px)').matches) {
          openLightbox(img.getAttribute('data-full-src') || img.src, img.alt);
        }
      });
    });
    lightbox.addEventListener('click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    var lightboxClose = lightbox.querySelector('[data-lightbox-close]');
    if (lightboxClose) lightboxClose.addEventListener('click', closeLightbox);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeLightbox();
    });
  }

  var galleryTrack = document.querySelector('[data-gallery-track]');
  var galleryDots = document.querySelector('[data-gallery-dots]');
  if (galleryTrack && galleryDots && 'IntersectionObserver' in window) {
    var dotEls = galleryDots.querySelectorAll('span');
    var plateEls = galleryTrack.querySelectorAll('[data-gallery-plate]');
    var dotObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio > 0.6) {
          var idx = Array.prototype.indexOf.call(plateEls, entry.target);
          dotEls.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        }
      });
    }, { root: galleryTrack, threshold: [0.6] });
    plateEls.forEach(function (p) { dotObserver.observe(p); });
  }

  // ---- Variant → gallery image sync -----------------------------------
  // Moves the existing gallery to whichever slide's data-media-id matches
  // the selected variant's featured image. Deliberately doesn't touch the
  // dot-sync IntersectionObserver set up just above: scrolling the target
  // slide into view (mobile track scroll or desktop scrollIntoView) makes
  // the browser cross that observer's own threshold naturally, so the
  // active dot updates itself through the existing, already-working code.
  if (galleryTrack) {
    var variantGalleryPlates = galleryTrack.querySelectorAll('[data-gallery-plate]');
    var currentGalleryMediaId = variantGalleryPlates.length
      ? variantGalleryPlates[0].getAttribute('data-media-id')
      : null;

    var goToVariantMedia = function (mediaId) {
      if (mediaId == null) return; // variant has no featured media — leave the gallery as-is
      if (String(mediaId) === String(currentGalleryMediaId)) return; // already showing it (e.g. only Size changed)

      var targetSlide = null;
      variantGalleryPlates.forEach(function (plate) {
        if (!targetSlide && plate.getAttribute('data-media-id') === String(mediaId)) targetSlide = plate;
      });
      if (!targetSlide) return; // media isn't in this gallery — nothing safe to do

      currentGalleryMediaId = mediaId;
      var behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      if (window.matchMedia('(min-width: 861px)').matches) {
        // Desktop keeps the existing stacked/sticky gallery — bring the
        // matching plate into view rather than building a carousel.
        targetSlide.scrollIntoView({ behavior: behavior, block: 'nearest' });
      } else {
        // Mobile: the existing working swipe track, using the slide's
        // real position rather than an assumed index/width.
        galleryTrack.scrollTo({ left: targetSlide.offsetLeft, behavior: behavior });
      }
    };

    document.addEventListener('maai:variant-change', function (e) {
      var detail = e.detail || {};
      // main-product.liquid supplies the exact Shopify featured_media.id.
      // Keep conservative fallbacks for compatibility with any older event payload.
      var variant = detail.variant;
      var mediaId = detail.mediaId;
      if (mediaId == null && variant && variant.featured_media) mediaId = variant.featured_media.id;
      if (mediaId == null && variant && variant.featured_image) mediaId = variant.featured_image.id;
      goToVariantMedia(mediaId);
    });
  }

  /* ---------------- Product-card image carousel ---------------- */
  // Desktop: arrow buttons scroll the track by one slide (click only ever
  // changes the image — it never opens the product page). Touch: the track
  // is a native horizontally-scrolling, scroll-snap element, so swiping is
  // handled entirely by the browser and a genuine swipe never fires the
  // card's click/navigation (only a true tap does), while a vertical drag
  // falls straight through to the page thanks to touch-action: pan-y.
  document.querySelectorAll('[data-card-carousel]').forEach(function (plate) {
    var track = plate.querySelector('[data-card-track]');
    var slides = track ? track.querySelectorAll('.plate-slide') : [];
    if (!track || slides.length < 2) return;

    var prevBtn = plate.querySelector('[data-card-prev]');
    var nextBtn = plate.querySelector('[data-card-next]');
    var dots = plate.querySelectorAll('[data-card-dots] span');
    var liveEl = plate.querySelector('[data-card-live]');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;

    var announce = function () {
      if (liveEl) liveEl.textContent = 'image ' + (index + 1) + ' of ' + slides.length;
    };
    var updateDots = function () {
      dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
    };
    var goTo = function (i) {
      index = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      updateDots();
      announce();
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(index - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        goTo(index + 1);
      });
    }

    // Keep the dots / index state in sync when the track is scrolled
    // directly (touch swipe or trackpad), without re-triggering goTo.
    var scrollTimer;
    track.addEventListener('scroll', function () {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        var next = Math.round(track.scrollLeft / track.clientWidth);
        if (next !== index && next >= 0 && next < slides.length) {
          index = next;
          updateDots();
        }
      }, 100);
    }, { passive: true });
  });

  /* ---------------- Quantity stepper ---------------- */
  document.querySelectorAll('[data-qty-stepper]').forEach(function (stepper) {
    var input = stepper.querySelector('input');
    var minus = stepper.querySelector('[data-qty-minus]');
    var plus = stepper.querySelector('[data-qty-plus]');
    if (!input) return;
    var step = function (delta) {
      var min = parseInt(input.min, 10) || 1;
      var next = (parseInt(input.value, 10) || min) + delta;
      input.value = Math.max(next, min);
    };
    if (minus) minus.addEventListener('click', function () { step(-1); });
    if (plus) plus.addEventListener('click', function () { step(1); });
  });

  /* ---------------- Mobile nav ---------------- */
  var navToggle = document.querySelector('[data-nav-toggle]');
  var navLinks = document.querySelector('[data-nav-links]');
  if (navToggle && navLinks) {
    navToggle.addEventListener('click', function () {
      navLinks.classList.toggle('is-open');
    });
  }

  /* ---------------- Inline audio previews ---------------- */
  document.querySelectorAll('[data-audio-trigger]').forEach(function (btn) {
    var audioSrc = btn.getAttribute('data-audio-src');
    if (!audioSrc) return;
    var audio = new Audio(audioSrc);
    var bar = btn.parentElement ? btn.parentElement.querySelector('[data-audio-progress]') : null;

    audio.addEventListener('timeupdate', function () {
      if (bar && audio.duration) {
        bar.style.width = (audio.currentTime / audio.duration) * 100 + '%';
      }
    });
    audio.addEventListener('ended', function () {
      btn.classList.remove('is-playing');
      if (bar) bar.style.width = '0%';
    });

    btn.addEventListener('click', function () {
      // pause any other playing preview first
      document.querySelectorAll('[data-audio-trigger].is-playing').forEach(function (other) {
        if (other !== btn) other.click();
      });
      if (audio.paused) {
        audio.play();
        btn.classList.add('is-playing');
      } else {
        audio.pause();
        btn.classList.remove('is-playing');
      }
    });
  });

  /* ---------------- Cart drawer ---------------- */
  var CartDrawer = {
    overlay: document.querySelector('[data-cart-overlay]'),
    drawer: document.querySelector('[data-cart-drawer]'),
    body: document.querySelector('[data-cart-body]'),
    subtotal: document.querySelector('[data-cart-subtotal]'),
    countEls: document.querySelectorAll('[data-cart-count]'),

    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.render();
    },
    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    },
    fetchCart: function () {
      return fetch('/cart.js').then(function (r) { return r.json(); });
    },
    updateCountBadges: function (count) {
      this.countEls.forEach(function (el) { el.textContent = count; });
    },
    render: function () {
      var self = this;
      if (!self.body) return;
      self.fetchCart().then(function (cart) {
        self.updateCountBadges(cart.item_count);

        if (cart.item_count === 0) {
          self.body.innerHTML = '<p class="cart-drawer-empty">Your cart is empty.</p>';
          if (self.subtotal) self.subtotal.textContent = '';
          return;
        }

        var html = '';
        cart.items.forEach(function (item) {
          html += '' +
            '<div class="cart-line" data-line-key="' + item.key + '">' +
              '<img src="' + (item.image ? item.image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_160x$1$2') : '') + '" alt="' + item.product_title + '">' +
              '<div>' +
                '<div class="cart-line-title">' + item.product_title + '</div>' +
                (item.variant_title ? '<div class="cart-line-variant">' + item.variant_title + '</div>' : '') +
                '<div class="cart-line-price">' + self.formatMoney(item.final_line_price) + '</div>' +
                '<div class="cart-line-qty">' +
                  '<button type="button" data-qty-decrease>&minus;</button>' +
                  '<span>' + item.quantity + '</span>' +
                  '<button type="button" data-qty-increase>+</button>' +
                '</div>' +
                '<button type="button" class="cart-line-remove" data-line-remove>Remove</button>' +
              '</div>' +
            '</div>';
        });
        self.body.innerHTML = html;

        if (self.subtotal) self.subtotal.textContent = self.formatMoney(cart.total_price);

        // wire up qty / remove controls
        self.body.querySelectorAll('[data-qty-increase]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var key = btn.closest('[data-line-key]').getAttribute('data-line-key');
            var qtyEl = btn.previousElementSibling;
            self.changeLine(key, parseInt(qtyEl.textContent, 10) + 1);
          });
        });
        self.body.querySelectorAll('[data-qty-decrease]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var key = btn.closest('[data-line-key]').getAttribute('data-line-key');
            var qtyEl = btn.nextElementSibling;
            var newQty = parseInt(qtyEl.textContent, 10) - 1;
            self.changeLine(key, Math.max(newQty, 0));
          });
        });
        self.body.querySelectorAll('[data-line-remove]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var key = btn.closest('[data-line-key]').getAttribute('data-line-key');
            self.changeLine(key, 0);
          });
        });
      });
    },
    changeLine: function (key, quantity) {
      var self = this;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function () { self.render(); });
    },
    formatMoney: function (cents) {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD'
      });
    },
    add: function (formData) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
        .then(function (r) { return r.json(); });
    }
  };
  window.CartDrawer = CartDrawer;

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      CartDrawer.open();
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      CartDrawer.close();
    }
  });

  // initial badge count on page load
  CartDrawer.fetchCart().then(function (cart) {
    CartDrawer.updateCountBadges(cart.item_count);
  });

  /* ---------------- Product forms (add to cart via Ajax) ---------------- */
  document.querySelectorAll('[data-product-form]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = form.querySelector('[type="submit"]');
      var formData = new FormData(form);
      var payload = { items: [{ id: formData.get('id'), quantity: parseInt(formData.get('quantity') || '1', 10) }] };

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.originalText = submitBtn.dataset.originalText || submitBtn.textContent;
        submitBtn.textContent = 'Adding…';
      }

      window.CartDrawer.add(payload).then(function () {
        if (submitBtn) {
          submitBtn.textContent = 'Added';
          submitBtn.classList.add('is-added');
        }
        // Let the confirmation register on the page itself before the
        // drawer takes over — otherwise it reads as the drawer opening
        // on its own rather than as a response to what you just did.
        window.setTimeout(function () {
          window.CartDrawer.open();
          window.setTimeout(function () {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = submitBtn.dataset.originalText;
              submitBtn.classList.remove('is-added');
            }
          }, 400);
        }, 550);
      }).catch(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Sold out or unavailable';
        }
      });
    });
  });

});
